/**
 * GlobalTimerBar — persistent floating bar(s) showing all running task timers.
 *
 * Appears at the bottom of the sidebar (desktop) or above the ActionPanel (mobile).
 * Multiple timers stack vertically, most recently started on top.
 *
 * Each bar shows: task title + live elapsed time + stop button.
 * Click a bar to navigate to the task.
 */

import { memo, useState, useEffect, useCallback } from "react"
import { stopTimer } from "@/db/operations"
import { useRevealTask } from "@/features/search/useRevealTask"
import type { DbObject } from "@/db/schema"
import { cn } from "@/shared/utils/general/cn"
import StopIcon from "@/shared/icons/pikaicons/solid/media/stop-circle.svg?react"

interface GlobalTimerBarProps {
  tasks: DbObject[]
  className?: string
  onNavigate?: () => void
}

/**
 * Format milliseconds to HH:MM:SS for live display.
 */
function formatLiveTimer(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  }
  return `${m}:${String(s).padStart(2, "0")}`
}

export const GlobalTimerBar = memo(function GlobalTimerBar({
  tasks,
  className,
  onNavigate,
}: GlobalTimerBarProps) {
  if (tasks.length === 0) return null
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {tasks.map((task) => (
        <TimerBarRow key={task.id} task={task} onNavigate={onNavigate} />
      ))}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Single timer row
// ---------------------------------------------------------------------------

const TimerBarRow = memo(function TimerBarRow({ task, onNavigate }: { task: DbObject; onNavigate?: () => void }) {
  const revealTask = useRevealTask()
  const taskId = task.id!

  // Live elapsed time
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!task.timerStartedAt) return
    const startMs = Number(task.timerStartedAt)
    const update = () => setElapsed(Date.now() - startMs)
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [task.timerStartedAt])

  const handleNavigate = useCallback((e: React.MouseEvent) => {
    // Don't navigate if stop button was clicked
    const target = e.target as HTMLElement
    if (target.closest("button[aria-label='Stop timer']")) return
    void revealTask(taskId)
    onNavigate?.()
  }, [taskId, revealTask, onNavigate])

  // Show current session elapsed only (matches task detail display)
  const title = task.title || "Untitled"

  return (
    <div
      className={cn(
        "global-timer-bar flex cursor-pointer items-center gap-2 rounded-lg border border-border/40 bg-background/95 px-3 py-1.5 shadow-sm backdrop-blur-sm transition-all hover:bg-accent/50",
        "animate-in slide-in-from-bottom-2 fade-in duration-200",
      )}
      data-timer-bar
      onClick={handleNavigate}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && void revealTask(taskId)}
    >
      {/* Pulsing recording dot */}
      <span className="relative flex size-2 shrink-0">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-400 opacity-75" />
        <span className="relative inline-flex size-2 rounded-full bg-red-500" />
      </span>

      {/* Task title — truncated */}
      <span className="min-w-0 flex-1 truncate text-xs font-medium">
        {title}
      </span>

      {/* Live timer */}
      <span className="shrink-0 text-xs font-semibold tabular-nums text-foreground">
        {formatLiveTimer(elapsed)}
      </span>

      {/* Stop button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          void stopTimer(taskId)
        }}
        className="shrink-0 rounded-md p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
        aria-label="Stop timer"
      >
        <StopIcon className="size-4" />
      </button>
    </div>
  )
})
